import { ICON_SIZE } from '@components/v2/constants';
import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { CircleAlert, CircleCheck, X } from 'lucide-react';
import type { ElementType } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useAlertStyles } from './alert.styles';
import type { AlertProps, AlertSeverity } from './alert.types';

const ICON_BY_SEVERITY: Record<AlertSeverity, ElementType> = {
  success: CircleCheck,
  error: CircleAlert,
  warning: CircleAlert,
  info: CircleAlert,
};

const Alert = ({
  title,
  message,
  severity = 'info',
  variant = 'inline',
  values,
  isLoading = false,
  onClose,
  testId,
}: AlertProps) => {
  const { translate } = useTranslation();
  const { classes } = useAlertStyles({ severity, isFloating: variant === 'floating' });

  return (
    <Box role={isLoading ? 'status' : 'alert'} test-id={testId} className={classes.root}>
      <Box className={classes.content}>
        <Box component="span" className={classes.icon}>
          {isLoading ? (
            <Loading size={ICON_SIZE.sm} color="inherit" />
          ) : (
            <Icon icon={ICON_BY_SEVERITY[severity]} size="md" />
          )}
        </Box>

        <Box className={classes.texts}>
          {title && (
            <Typography
              variant="subtitle2"
              color="text.primary"
              testId={testId ? `${testId}-title` : undefined}
              className={classes.title}
            >
              {translate(title as LangEnum, values)}
            </Typography>
          )}
          {message && (
            <Typography
              variant="body2"
              color="text.secondary"
              testId={testId ? `${testId}-message` : undefined}
              className={classes.message}
            >
              {translate(message as LangEnum, values)}
            </Typography>
          )}
        </Box>
      </Box>

      {onClose && !isLoading && (
        <Box className={classes.closeSlot}>
          <IconButton
            icon={X}
            size="small"
            shape="round"
            ariaLabel={translate(LangEnum.GLOBAL_CLOSE)}
            testId={testId ? `${testId}-close` : undefined}
            className={classes.closeButton}
            onClick={onClose}
          />
        </Box>
      )}
    </Box>
  );
};

export default Alert;
